import React, { useState } from "react";
import { useLang } from "../lib/lang";
import { useSeo } from "../lib/seo";

const QA = [
  {
    en: ["Is this an agency?", "No. Brand and Behavior is a productized brand service. You choose a service with a fixed scope and price, you complete an intake, and you receive a strategic deliverable. No back-and-forth, no calls (except inside Brand Office)."],
    es: ["¿Esto es una agencia?", "No. Brand and Behavior es un servicio de marca productizado. Eliges un servicio con alcance y precio fijo, completas el intake y recibes un entregable estratégico. Sin idas y vueltas, sin llamadas (excepto dentro de Brand Office)."],
  },
  {
    en: ["What exactly do I receive?", "Strategic deliverables you can act on the same week: written strategy documents (positioning, narrative, messaging), direction for designers and developers (briefs they can ship from), roadmaps that survive the quarter, brand systems and visual universe direction, a clear status tracker inside your project portal, and a recommended next step at the end of every delivery."],
    es: ["¿Qué recibo exactamente?", "Entregables estratégicos sobre los que puedes actuar la misma semana: documentos de estrategia escritos (posicionamiento, narrativa, mensaje), dirección para diseñadores y developers (briefs ejecutables), roadmaps que sobreviven el trimestre, sistemas de marca y dirección de universo visual, un status tracker claro dentro de tu portal de proyecto, y un próximo paso recomendado al cierre de cada entrega."],
  },
  {
    en: ["How do we communicate?", "All work happens through written briefs, structured intake forms, documents and recorded walk-throughs. No live calls by default. This protects your time and produces better thinking. Brand Office is the exception and includes weekly calls."],
    es: ["¿Cómo nos comunicamos?", "Todo el trabajo sucede mediante briefs escritos, intake forms estructurados, documentos y walk-throughs grabados. Sin llamadas en vivo por defecto. Esto protege tu tiempo y produce mejor pensamiento. Brand Office es la excepción e incluye llamadas semanales."],
  },
  {
    en: ["What about revisions?", "Brand Development and Brand Strategy include 2 adjustment rounds. Brand Scan is a diagnosis with no revisions. Brand Partner is request-based. Brand Office is consulting."],
    es: ["¿Y las revisiones?", "Brand Development y Brand Strategy incluyen 2 rondas de ajuste. Brand Scan es un diagnóstico sin revisiones. Brand Partner se basa en requests. Brand Office es consulting."],
  },
  {
    en: ["What is NOT included?", "Logo design, final brand book, full website, ads management, social posting, content production, and unlimited revisions. We give you strategy and direction, not production."],
    es: ["¿Qué NO está incluido?", "Diseño de logo, brand book completo, web completa, gestión de ads, posteo en redes, producción de contenido y revisiones ilimitadas. Damos estrategia y dirección, no producción."],
  },
  {
    en: ["Who is this for?", "Founders, ventures, and leadership teams entering their next stage. About to launch, reposition, expand, raise capital, or turn a complex idea into a brand people can understand and believe in."],
    es: ["¿Para quién es?", "Fundadores, ventures y equipos directivos que entran en su siguiente etapa. A punto de lanzar, reposicionar, expandirse, levantar capital o convertir una idea compleja en una marca que la gente entienda y crea."],
  },
  {
    en: ["When do I pay?", "Upfront for Brand Scan, Development and Strategy. Monthly for Brand Partner. Brand Office is an application. Price is provided after we review fit."],
    es: ["¿Cuándo pago?", "Por adelantado en Brand Scan, Development y Strategy. Mensual en Brand Partner. Brand Office es una aplicación. El precio se entrega tras revisar el encaje."],
  },
  {
    en: ["What if my request lacks context?", "We mark it “Needs clarification.” It does not count against your monthly limit until accepted. Good requests get faster, better answers."],
    es: ["¿Y si mi request no tiene suficiente contexto?", "Lo marcamos como “Necesita clarificación”. No cuenta contra tu límite mensual hasta que sea aceptado. Buenos requests reciben respuestas más rápidas y mejores."],
  },
];

export default function FaqPage() {
  const { t, lang } = useLang();
  const [open, setOpen] = useState(0);
  useSeo({
    title: lang === "es"
      ? "Preguntas frecuentes — Brand and Behavior"
      : "FAQ — Brand and Behavior",
    description: lang === "es"
      ? "Cómo trabajamos, qué recibes, cuándo se paga, qué no incluye. Preguntas frecuentes de Brand and Behavior."
      : "How we work, what you receive, when you pay, what's not included. Common questions about Brand and Behavior.",
    path: lang === "es" ? "/es/faq" : "/faq",
  });

  return (
    <main className="app-main" data-testid="faq-page">
      <section className="page-hero">
        <div className="kicker">FAQ</div>
        <h1>{t("How we work", "Cómo trabajamos")}</h1>
        <p>{t("Clear scope, clear rules. Read these once and the rest of the process flows.", "Alcance claro, reglas claras. Léelas una vez y el resto del proceso fluye.")}</p>
      </section>
      <section style={{ padding: "0 22px 80px" }}>
        {QA.map((qa, i) => {
          const [q, a] = lang === "es" ? qa.es : qa.en;
          const isOpen = open === i;
          return (
            <div key={qa.en[0]} style={{ borderBottom: "1px solid var(--line)" }}>
              <button
                onClick={() => setOpen(isOpen ? -1 : i)}
                data-testid={`faq-q-${i}`}
                aria-expanded={isOpen}
                style={{
                  width: "100%", textAlign: "left", background: "transparent", border: 0,
                  padding: "26px 0", cursor: "pointer", display: "flex", justifyContent: "space-between",
                  alignItems: "baseline", gap: 18,
                  fontFamily: "inherit", color: "var(--ink)",
                  fontSize: "clamp(20px, 2vw, 26px)", letterSpacing: "-.024em", fontWeight: 500,
                }}
              >
                <span>{q}</span>
                <span style={{ fontFamily: "Instrument Serif", fontStyle: "italic", fontSize: 28 }}>{isOpen ? "—" : "+"}</span>
              </button>
              {isOpen && <div style={{ padding: "0 0 22px", fontSize: 16, lineHeight: 1.55, color: "var(--muted)", maxWidth: 860 }}>{a}</div>}
            </div>
          );
        })}
      </section>
    </main>
  );
}
